import { useEffect, useRef } from 'react';
import { resolveGroupMemberMode } from './customGroupsHelpers';

// Consolidates the two `setPortfolioMode`-pushing effects (originally
// triggered on [selectedFund] and [videoModes] separately) into a single
// effect that handles both triggers and preserves the subtle distinction
// between them:
//
// - Trigger = selectedFund changed: this is a user action (member-row
//   click or programmatic snap). Update the parent's ticker `state`,
//   honour the `skipFundEffectRef` gate (set by snap helpers that have
//   already pushed selection themselves), and — for a fund change inside
//   a group — flip the banner intent to "member preview" so BuilderPage
//   renders the per-fund banner rather than the group-level one.
//
// - Trigger = videoModes changed: a re-push needed because the per-fund
//   contentType data finished loading (or got mutated by a toggle). Must
//   preserve whatever banner intent was last applied — read
//   isGroupMemberPreviewRef without writing it.
//
// A prevFundRef compares the previous selectedFund object to detect which
// trigger fired this effect run, so the two semantics stay separated even
// inside a single effect.
export function useSelectionSync({
    selectedFund, selectedGroup, videoModes,
    pendingRef, readyRef,
    selectedGroupStatusRef, isGroupMemberPreviewRef, skipFundEffectRef,
    setPortfolioMode, setState,
}) {
    const prevFundRef = useRef(null);

    useEffect(() => {
        if (!selectedFund) {
            prevFundRef.current = null;
            return;
        }
        const fundChanged = prevFundRef.current !== selectedFund;
        prevFundRef.current = selectedFund;

        if (fundChanged && setState) {
            setState(selectedFund.fundNumber);
        }
        if (!setPortfolioMode) return;

        // Consume the snap gate on the first run after a snap helper set it,
        // even when the snapped-to fund is the already-selected object (e.g.
        // grouping the currently selected portfolio snaps to itself as the
        // group's primary). Leaving the flag set would swallow the NEXT real
        // selection's push, stranding BuilderPage on a stale group context.
        if (skipFundEffectRef && skipFundEffectRef.current) {
            skipFundEffectRef.current = false;
            return;
        }

        if (fundChanged && selectedGroup && isGroupMemberPreviewRef) {
            isGroupMemberPreviewRef.current = true;
        }

        if (selectedGroup) {
            const pool = selectedGroupStatusRef.current === 'ready' ? readyRef.current : pendingRef.current;
            const members = pool.filter(p => selectedGroup.fundNumbers.includes(p.fundNumber));
            setPortfolioMode({
                mode: resolveGroupMemberMode(selectedGroup, selectedFund, videoModes),
                fundNumber: selectedFund.fundNumber,
                name: selectedGroup.name,
                members,
                status: selectedGroupStatusRef.current || 'pending',
                isMemberPreview: isGroupMemberPreviewRef ? isGroupMemberPreviewRef.current : false,
                linked: selectedGroup.linked !== false,
            });
        } else {
            const status = readyRef.current.some(p => p.fundNumber === selectedFund.fundNumber) ? 'ready' : 'pending';
            setPortfolioMode({
                mode: videoModes[selectedFund.fundNumber] || 'editor',
                fundNumber: selectedFund.fundNumber,
                name: selectedFund.fundName,
                status,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFund, selectedGroup, videoModes]);
}
